import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles, Loader2 } from 'lucide-react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Aviso *obrigatório* (tela cheia, sem dismiss) que aparece quando uma nova
 * versão do app está disponível. O usuário precisa tocar em "Atualizar agora"
 * pra continuar — não dá pra fechar nem usar o app antes disso.
 *
 * Por que assim:
 *  - O Six Pass tem mudanças de UX/contrato frequentes na fase de teste.
 *  - Versão antiga rodando em PWA instalado gera bugs e confusão.
 *  - O cara não pode usar uma tela quebrada só porque ainda não recarregou.
 *
 * Detecção em duas frentes (independentes):
 *   1. Service Worker — `useRegisterSW` dispara `onNeedRefresh` quando há SW
 *      em estado `waiting`. Funciona bem no Chrome desktop e Android.
 *   2. Build-id check — fetch periódico em `/build-id.json` (no-cache) compara
 *      o ID baked-in (via `import.meta.env.VITE_BUILD_ID`) com o do servidor.
 *      Funciona em qualquer plataforma, inclusive iOS PWA standalone (onde o
 *      SW lifecycle é notoriamente bugado).
 *
 * Frequência de checagem:
 *   - A cada 20s (timer)
 *   - Quando a aba volta a ficar visível
 *   - No evento `pageshow` (iOS PWA volta do background)
 *   - No load inicial
 */

// Injetado em vite.config.ts via `define`. Cada build tem um ID único.
const CURRENT_BUILD_ID =
  ((import.meta as { env?: { VITE_BUILD_ID?: string } }).env?.VITE_BUILD_ID as string) || 'dev';

const VERSION_CHECK_INTERVAL_MS = 20_000;
const BUILD_ID_URL = '/build-id.json';

export function UpdatePrompt() {
  const [serverHasNewBuild, setServerHasNewBuild] = useState(false);
  const [updating, setUpdating] = useState(false);
  const checkingRef = useRef(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      console.info('[UpdatePrompt] SW registrado em', swUrl);
      if (!registration) return;

      const triggerCheck = (reason: string) =>
        registration
          .update()
          .then(() => console.info('[UpdatePrompt] SW check ok:', reason))
          .catch((err) => console.warn('[UpdatePrompt] SW check falhou:', reason, err));

      const interval = window.setInterval(
        () => triggerCheck('interval'),
        VERSION_CHECK_INTERVAL_MS,
      );

      const onVisibility = () => {
        if (document.visibilityState === 'visible') triggerCheck('visibility');
      };
      document.addEventListener('visibilitychange', onVisibility);

      const onShow = () => triggerCheck('pageshow');
      window.addEventListener('pageshow', onShow);

      return () => {
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pageshow', onShow);
      };
    },
    onNeedRefresh() {
      console.info('[UpdatePrompt] SW: nova versão disponível');
    },
    onOfflineReady() {
      console.info('[UpdatePrompt] pronto pra usar offline');
    },
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Detecção paralela via build-id (backup do SW)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Skip em dev: o Vite dev server faz SPA fallback e devolve index.html
    // ao invés de /build-id.json, gerando SyntaxError ruidoso (WARN-01 do
    // QA report) a cada VERSION_CHECK_INTERVAL_MS. Em prod o arquivo existe
    // (gerado em build time) — feature funciona normal.
    if (import.meta.env.DEV) return;

    let cancelled = false;

    const checkBuildId = async () => {
      if (checkingRef.current || cancelled) return;
      checkingRef.current = true;
      try {
        const res = await fetch(`${BUILD_ID_URL}?t=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data = (await res.json()) as { id?: string };
        if (cancelled) return;
        if (data.id && data.id !== CURRENT_BUILD_ID) {
          console.info(
            '[UpdatePrompt] build-id divergente — local=%s server=%s',
            CURRENT_BUILD_ID,
            data.id,
          );
          setServerHasNewBuild(true);
        }
      } catch (err) {
        console.warn('[UpdatePrompt] build-id check falhou', err);
      } finally {
        checkingRef.current = false;
      }
    };

    // Checa logo na inicialização (com leve delay pra não competir com o
    // primeiro paint).
    const initialTimeout = window.setTimeout(checkBuildId, 1500);

    const interval = window.setInterval(checkBuildId, VERSION_CHECK_INTERVAL_MS);

    const onVis = () => {
      if (document.visibilityState === 'visible') checkBuildId();
    };
    document.addEventListener('visibilitychange', onVis);

    const onShow = () => checkBuildId();
    window.addEventListener('pageshow', onShow);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeout);
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onShow);
    };
  }, []);

  const updateRequired = needRefresh || serverHasNewBuild;

  const handleUpdate = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      // Caminho preferencial: SW detectou e tem `waiting` pendente.
      // `updateServiceWorker(true)` chama skipWaiting + recarrega a página.
      if (needRefresh) {
        await updateServiceWorker(true);
        // Em caso raro de não recarregar, força:
        window.setTimeout(() => window.location.reload(), 1500);
        return;
      }

      // Caminho de fallback: server tem versão nova mas o SW não detectou
      // (iOS PWA bugado). Limpa caches, desregistra SW, e força hard reload
      // pra garantir que a próxima inicialização carregue tudo do zero.
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // Cache-busting query pra forçar fresh fetch do index.html.
      window.location.replace(`${window.location.pathname}?_t=${Date.now()}`);
    } catch (err) {
      console.error('[UpdatePrompt] erro ao atualizar — fallback pra reload simples', err);
      window.location.reload();
    }
  };

  return (
    <AnimatePresence>
      {updateRequired && (
        <motion.div
          key="force-update-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="force-update-title"
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/95 backdrop-blur-xl p-6"
          style={{
            paddingTop: 'max(1.5rem, var(--safe-top, 0px))',
            paddingBottom: 'max(1.5rem, var(--safe-bottom, 0px))',
          }}
          // sem onClick — bloqueia interação com o app por baixo
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0a0a14] p-7 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          >
            <div className="mb-5 flex items-center justify-center">
              <motion.div
                animate={{ rotate: [0, -8, 8, 0], scale: [1, 1.04, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500 shadow-[0_18px_40px_rgba(236,72,153,0.35)]"
              >
                <Sparkles size={28} className="text-white" />
              </motion.div>
            </div>

            <h2
              id="force-update-title"
              className="mb-2 text-center text-xl font-black text-white tracking-tight"
            >
              Nova versão disponível
            </h2>
            <p className="mb-6 text-center text-sm leading-relaxed text-slate-300">
              Atualizamos o Six Pass com melhorias e correções importantes. Toca em atualizar pra
              continuar com a versão mais nova.
            </p>

            <button
              type="button"
              onClick={handleUpdate}
              disabled={updating}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-pink-500 to-blue-500 py-3.5 font-bold text-white shadow-lg shadow-blue-500/20 transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {updating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Atualizando…
                </>
              ) : (
                'Atualizar agora'
              )}
            </button>

            <p className="mt-4 text-center text-[11px] text-slate-400">
              A atualização leva 2-3 segundos. Seus dados continuam salvos.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
